import TablePagination from "@material-ui/core/TablePagination";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import AdvertiserDashboardMetrics from "../../components/redesign/modules/AdvertiserDashboard/AdvertiserDashboardMetrics";
import BackButton from "../../components/redesign/elements/BackButton";
import BulkActionConfirmModal from "../../components/redesign/modules/AdvertiserDashboard/BulkActionConfirmModal";
import CampaignBulkActionsBar from "../../components/redesign/modules/AdvertiserDashboard/CampaignBulkActionsBar";
import CampaignsEmptyState from "../../components/redesign/modules/AdvertiserDashboard/CampaignsEmptyState";
import Loader from "../../components/elements/Loader";
import api from "../../api";
import csvDownload from "../../utils/csvExport";
import CampaignsSectionToolbar, {
  CampaignFilters,
} from "../../components/redesign/modules/AdvertiserDashboard/CampaignsSectionToolbar";
import CampaignsTable, { SortCol, SortDir } from "../../components/redesign/modules/AdvertiserDashboard/CampaignsTable";
import {
  BrandgenCampaignPool,
  BulkCampaignActionResult,
  CampaignListResponse,
  ReportTimeFrames,
} from "../../types/campaignTypes";
import { BulkAction, PAST_TENSE } from "../../components/redesign/modules/AdvertiserDashboard/campaignBulkActions";
import {
  DASHBOARD_REPORT_TIMEFRAME,
  computeDashboardMetrics,
  mapCampaignsToTableRows,
} from "../../components/redesign/modules/AdvertiserDashboard/campaignUtils";
import {
  DEFAULT_EXPORT_TIME_FRAME,
  EXPORT_FILENAME,
  buildCampaignExportRows,
} from "../../components/redesign/modules/AdvertiserDashboard/campaignExport";
import { ROUTE_PATHS } from "../../types/RoutePaths";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { errorToaster, infoToaster, successToaster } from "../../helpers/toasters";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useSetPageMeta } from "../../layouts/AppShellLayout/PageMetaContext";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const PaginationWrap = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 8,
  "& .MuiTablePagination-root": {
    color: theme.palette.brand.gray700,
    fontSize: 13,
    overflow: "visible",
  },
  "& .MuiTablePagination-caption": {
    fontSize: 13,
    color: theme.palette.brand.gray700,
  },
  "& .MuiTablePagination-select": {
    fontSize: 13,
    color: theme.palette.brand.gray700,
  },
  "& .MuiTablePagination-selectIcon": {
    color: theme.palette.brand.gray500,
  },
  "& .MuiIconButton-root": {
    color: theme.palette.brand.gray700,
    "&:hover": {
      background: theme.palette.brand.gray100,
    },
    "&.Mui-disabled": {
      color: theme.palette.brand.gray300,
    },
  },
}));

const SORT_COL_TO_DB: Record<SortCol, string> = {
  name: "name",
  start_date: "start_date",
  end_date: "end_date",
  spend: "totalSpend",
  impressions: "impressions",
  clicks: "clicks",
  ctr: "ctr",
};

const AdvertiserDashboardPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const isAdminView = location.pathname.startsWith("/administration");
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency?.advertisers) ?? [];

  const advertiser = useMemo(() => advertisers.find((item) => item.brandgenAdvertiserId === id), [advertisers, id]);
  const storedAdvertiserName = advertiser?.name;
  const [fetchedAdvertiserName, setFetchedAdvertiserName] = useState<string | null>(null);
  const advertiserName = storedAdvertiserName ?? fetchedAdvertiserName ?? "Advertiser";
  useSetPageMeta("Dashboard", `Campaign performance for ${advertiserName}`);

  const [campaigns, setCampaigns] = useState<BrandgenCampaignPool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => {
    const p = new URLSearchParams(location.search).get("page");
    return p ? Math.max(0, Number.parseInt(p, 10) - 1) : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const r = new URLSearchParams(location.search).get("rowsPerPage");
    const parsed = r ? Number.parseInt(r, 10) : 10;
    return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : 10;
  });
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir }>(() => {
    const p = new URLSearchParams(location.search);
    const col = p.get("sortBy") as SortCol;
    const dir = p.get("sortDir") as SortDir;
    return {
      col: col && col in SORT_COL_TO_DB ? col : "start_date",
      dir: dir === "ASC" ? "ASC" : "DESC",
    };
  });
  const [filters, setFilters] = useState<CampaignFilters>(() => {
    const p = new URLSearchParams(location.search);
    return {
      search: p.get("search") || "",
      displayStatus: p.get("displayStatus") || "",
      campaignType: p.get("campaignType") || "",
      startDateFrom: p.get("startDateFrom") || "",
      endDateTo: p.get("endDateTo") || "",
    };
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [exportTimeFrame, setExportTimeFrame] = useState<ReportTimeFrames>(DEFAULT_EXPORT_TIME_FRAME);
  const [isExporting, setIsExporting] = useState(false);

  const { debouncedValue: debouncedSearch } = useDebouncedValue(filters.search, 800);

  const handleFilterChange = useCallback((key: keyof CampaignFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  }, []);

  const handleSortChange = useCallback((col: SortCol) => {
    setSort((prev) => (prev.col === col ? { col, dir: prev.dir === "ASC" ? "DESC" : "ASC" } : { col, dir: "DESC" }));
  }, []);

  const loadCampaigns = useCallback(async () => {
    try {
      setIsLoading(true);
      const { payload } = await api.getCampaignsByAdvertiserPaginated(id, {
        page: page + 1, // API is 1-based
        limit: rowsPerPage,
        sortBy: SORT_COL_TO_DB[sort.col],
        sortDir: sort.dir,
        // Metric sorting happens in SQL against the same report row the table displays.
        sortTimeFrame: DASHBOARD_REPORT_TIMEFRAME,
        search: debouncedSearch || undefined,
        displayStatus: filters.displayStatus || undefined,
        campaignType: filters.campaignType || undefined,
        startDateFrom: filters.startDateFrom || undefined,
        endDateTo: filters.endDateTo || undefined,
      });
      const response = payload as CampaignListResponse;
      const data = response.data ?? [];
      setCampaigns(data);
      setTotal(response.total ?? 0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("fetchAdvertiserCampaigns error:", error);
      setCampaigns([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    id,
    page,
    rowsPerPage,
    sort,
    debouncedSearch,
    filters.displayStatus,
    filters.campaignType,
    filters.startDateFrom,
    filters.endDateTo,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (storedAdvertiserName) {
      setFetchedAdvertiserName(storedAdvertiserName);
    } else {
      const loadAdvertiser = async () => {
        try {
          const { payload } = await api.getAdvertiserById(id);
          if (!cancelled && payload?.name) {
            setFetchedAdvertiserName(payload.name);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log("fetchAdvertiserById error:", error);
        }
      };

      loadAdvertiser();
    }

    return () => {
      cancelled = true;
    };
  }, [storedAdvertiserName, id]);

  useEffect(() => {
    if (isAdminView) return;
    localStorage.setItem("currentBrandgenAdvertiserId", id);
  }, [id, isAdminView]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.displayStatus) params.set("displayStatus", filters.displayStatus);
    if (filters.campaignType) params.set("campaignType", filters.campaignType);
    if (filters.startDateFrom) params.set("startDateFrom", filters.startDateFrom);
    if (filters.endDateTo) params.set("endDateTo", filters.endDateTo);
    params.set("sortBy", sort.col);
    params.set("sortDir", sort.dir);
    if (page > 0) params.set("page", String(page + 1));
    if (rowsPerPage !== 10) params.set("rowsPerPage", String(rowsPerPage));
    history.replace({ pathname: location.pathname, search: params.toString() });
  }, [filters, sort, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selection is scoped to the campaigns currently on screen.
  useEffect(() => {
    setSelectedIds([]);
  }, [page, rowsPerPage, sort, filters]);

  const dashboardMetrics = useMemo(() => computeDashboardMetrics(campaigns), [campaigns]);
  const campaignRows = useMemo(() => mapCampaignsToTableRows(campaigns), [campaigns]);
  const selectedRows = useMemo(
    () => campaignRows.filter((row) => selectedIds.includes(row.id)),
    [campaignRows, selectedIds],
  );

  const handleToggleRow = useCallback((campaignId: string) => {
    setSelectedIds((prev) =>
      prev.includes(campaignId) ? prev.filter((rowId) => rowId !== campaignId) : [...prev, campaignId],
    );
  }, []);

  const handleToggleAll = useCallback(() => {
    const selectableIds = campaignRows.filter((row) => !row.processing).map((row) => row.id);
    setSelectedIds((prev) => (selectableIds.every((rowId) => prev.includes(rowId)) ? [] : selectableIds));
  }, [campaignRows]);

  const handleExport = useCallback(async () => {
    const campaignsToExport = campaigns.filter((campaign) => selectedIds.includes(campaign.brandgenCampaignPoolId));

    if (campaignsToExport.length === 0) {
      infoToaster("Please, select campaigns to export");
      return;
    }

    setIsExporting(true);
    try {
      const data = await buildCampaignExportRows(campaignsToExport, exportTimeFrame);
      csvDownload({ data, filename: EXPORT_FILENAME, delimiter: "," });
      setSelectedIds([]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("campaign export error:", error);
      errorToaster("Something went wrong while exporting the campaigns. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [campaigns, selectedIds, exportTimeFrame]);

  const handleConfirmAction = useCallback(async () => {
    if (!pendingAction) return;

    setIsApplying(true);
    try {
      const { payload } = await (pendingAction === "activate" || pendingAction === "deactivate"
        ? api.bulkUpdateCampaignStatus(selectedIds, pendingAction === "activate")
        : api.bulkArchiveCampaigns(selectedIds, pendingAction === "archive"));

      const { succeeded = [], failed = [] } = (payload ?? {}) as BulkCampaignActionResult;

      if (failed.length === 0) {
        successToaster(
          `${succeeded.length} campaign${succeeded.length === 1 ? "" : "s"} ${PAST_TENSE[pendingAction]}.`,
        );
      } else {
        errorToaster(
          `${succeeded.length} of ${succeeded.length + failed.length} campaigns ${PAST_TENSE[pendingAction]}, ${
            failed.length
          } failed.`,
          6000,
        );
      }

      await loadCampaigns();
      setSelectedIds([]);
      setPendingAction(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("bulk campaign action error:", error);
      errorToaster("Something went wrong while updating the campaigns. Please try again.");
    } finally {
      setIsApplying(false);
    }
  }, [pendingAction, selectedIds, loadCampaigns]);

  const handleNavigateToCampaign = useCallback(
    (campaignId: string) => {
      history.push(
        isAdminView ? `/administration/${id}/campaigns/${campaignId}` : `/advertisers/${id}/campaigns/${campaignId}`,
      );
    },
    [history, id, isAdminView],
  );

  const handleCreateCampaign = useCallback(() => {
    history.push(ROUTE_PATHS.CUSTOM_CAMPAIGN_CREATE);
  }, [history]);

  return (
    <>
      {isAdminView ? <BackButton label="Back to Administration" to={ROUTE_PATHS.ADMINISTRATION} /> : null}
      <AdvertiserDashboardMetrics {...dashboardMetrics} />
      <CampaignsSectionToolbar
        advertiserName={advertiserName}
        filters={filters}
        onFilterChange={handleFilterChange}
        onCreateCampaign={handleCreateCampaign}
      />
      {isLoading ? (
        <Loader />
      ) : campaignRows.length ? (
        <>
          <CampaignBulkActionsBar
            selectedRows={selectedRows}
            onAction={setPendingAction}
            onClearSelection={() => setSelectedIds([])}
            isApplying={isApplying}
            exportTimeFrame={exportTimeFrame}
            onExportTimeFrameChange={setExportTimeFrame}
            onExport={handleExport}
            isExporting={isExporting}
          />
          <CampaignsTable
            rows={campaignRows}
            sort={sort}
            onSortChange={handleSortChange}
            onRowClick={handleNavigateToCampaign}
            selectedIds={selectedIds}
            onToggleRow={handleToggleRow}
            onToggleAll={handleToggleAll}
          />
          <PaginationWrap>
            <TablePagination
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              page={page}
              onChangePage={(_e, newPage) => setPage(newPage)}
              onChangeRowsPerPage={(e) => {
                setRowsPerPage(Number.parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </PaginationWrap>
        </>
      ) : (
        <CampaignsEmptyState onCreateCampaign={isAdminView ? undefined : handleCreateCampaign} />
      )}
      {pendingAction ? (
        <BulkActionConfirmModal
          action={pendingAction}
          count={selectedIds.length}
          isApplying={isApplying}
          onConfirm={handleConfirmAction}
          onClose={() => setPendingAction(null)}
        />
      ) : null}
    </>
  );
};

export default AdvertiserDashboardPage;
